<script>
	import Popover from '@components/general/popover.svelte'
	import { onMount, untrack } from 'svelte'
  import debounce from 'debounce'
  import { LoaderCircle, Ellipsis } from 'lucide-svelte'
  import Fuse from 'fuse.js'
  import { watch } from 'runed'
  import { cn } from '@lib/utils'

	let { 
    open = $bindable(false),
    value = $bindable(null),
    items = $bindable([]),
    labelKey = "label",
    valueKey = "value",
    queryKey = "q",
    placeholder = "Rechercher...",
    autocomplete = "off",
    load,
    url,
    name,
    error,
    patternMessage,
    loadAll = false,
    fetchInitial = true,
    fetchOnUrlChange = false,
    filter = true,
    fuseKeys,
    onQuery,
    onValueChange,
    onClear,
    ...inputProps
  } = $props()

	let index = $state(0)
	let query = $state("")
  let typing = $state(false)
  let loading = $state(false)
  let itemRefs = $state([])
  let inputRef = $state(null)
  let lastSyncedValue = $state(null)

  const debouncedLoad = load ? debounce(load, 300) : null
  const debouncedFetch = url ? debounce(fetchFromUrl, 300) : null
  const debouncedOnQuery = onQuery ? debounce(onQuery, 300) : null

	onMount(async () => {
    if (url) {
      if (loadAll) {
        await fetchFromUrl({ includeQuery: false, includeIds: !loadAll })
      } else if (fetchInitial && value) {
        await fetchFromUrl({ includeQuery: false, includeIds: !loadAll })
        onValueChange?.(selectedItem, { initial: true })
      }
    }
		// selectedItem && selectItem(selectedItem)
    if (selectedItem) {
      selectItem(selectedItem, null, { triggerCallback: false })
      query = selectedItem[labelKey]
    }
	})
  
  // An effect that smooth scrolls the highlighted item into view
  $effect(() => {
    if (itemRefs[index]) {
      itemRefs[index]?.scrollIntoView({ behavior: "instant", block: "nearest" })
    }
  })

	let filteredItems = $derived.by(() => {
    if (!query?.length || !filter) return items
    if (fuseKeys) {
      const fuse = new Fuse(items, {
        includeScore: true,
        threshold: 0.2,
        keys: fuseKeys || [labelKey]
      })
      return fuse.search(query).map(result => result.item)
    } else {
		return items.filter((item) => {
			return item[labelKey].toLowerCase().includes(query.toLowerCase())
		})
  }
})

	let highlightedItem = $derived(filteredItems[index])
	let selectedItem = $derived(value && items.find(item => item[valueKey] === value))

  // Keep internal UI (query/open/index) in sync when `value` is changed externally
  // (e.g. Inertia `reset()` clears the bound value but does not trigger `clearValue()`).
  //
  // Important: we do NOT mutate `items` here because `items` can be bound to parent state,
  // and clearing parent-owned arrays from inside the component is a common source of reactive loops.
  $effect(() => {
    if (value === null || value === undefined || value === "") {
      const isFocused =
        typeof document !== "undefined" &&
        inputRef &&
        document.activeElement === inputRef
      const hasQuery = untrack(() => Boolean(query?.length))
      const preserveQuery = isFocused && hasQuery

      untrack(() => {
        open = false
        index = 0
        typing = false
        if (!preserveQuery) query = ""
      })
      lastSyncedValue = null
    }
  })

  // Keep UI in sync when `value` is changed externally, but don't fight the user while typing.
  // Important: do not depend on `query` here, otherwise any manual edits will be overwritten.
  $effect(() => {
    if (value === null || value === undefined || value === "") return
    if (!selectedItem) return
    if (value === lastSyncedValue) return

    untrack(() => {
      query = selectedItem[labelKey]
    })
    lastSyncedValue = value
  })

  async function fetchFromUrl({ includeQuery = true, includeIds = false } = {}) {
    typing = false
    if (!url || (!query.length && !loadAll && !value)) return
    loading = true
    const fetchUrl = new URL([window.location.origin, url].join('/'))
    if (includeQuery) fetchUrl.searchParams.set(queryKey, query)
    if (includeIds) fetchUrl.searchParams.append("ids[]", value)
    const response = await fetch(fetchUrl.toString())
    const data = await response.json()
    items = data
    loading = false
  }
	
  function handleUpKey(e) {
    e.preventDefault()
    e.stopPropagation()
    index = e.shiftKey ? 0 : Math.max(index - 1, 0)
  }

  function handleDownKey(e) {
    e.preventDefault()
    if (!open) return open = true
    index = e.shiftKey ? filteredItems.length - 1 : Math.min(index + 1, filteredItems.length - 1)
  }

  function handleEnterKey(e) {
    e.preventDefault()
    e.stopPropagation()
    if (Boolean(highlightedItem)) {
      handleSelect()
      closeList()
    }
  }

  function handleEscapeKey() {
    closeList()
  }

	function handleKeydown(e) {
    if (e.key === "ArrowUp") {
      handleUpKey(e)
    } else if (e.key === "ArrowDown") {
      handleDownKey(e)
    } else if (e.key === "Enter") {
      handleEnterKey(e)
    } else if (e.key === "Escape") {
      handleEscapeKey()
    }
	}

  function validate() {
    const validity = inputRef?.validity
    error = (validity.patternMismatch && patternMessage) ? patternMessage : inputRef.validationMessage
  }

  function handleBlur() {
    inputProps.onblur?.()
    validate()
    closeList()
  }

  function handleInput(e) {
    inputProps.oninput?.()
    onInput(e)
  }

	async function onInput(e) {
    index = 0
    open = true

    // If the user edits the text, the previously selected value is no longer valid.
    // Clear it immediately so backspace/typing works as expected.
    const selected = selectedItem
    if (!query.length) {
      if (value !== null && value !== undefined && value !== "") {
        value = null
        onClear?.()
        onValueChange?.(null)
      }
    } else if (selected && query !== selected[labelKey]) {
      value = null
      onClear?.()
      onValueChange?.(null)
    }

    if (url && !loadAll) {
      typing = true
      await debouncedFetch()
    }
    if (load) await debouncedLoad()
    debouncedOnQuery?.(query)
	}

	function handleSelect() {
		if (highlightedItem) {
      const chosen = highlightedItem
			value = chosen[valueKey]
			query = chosen[labelKey]
      onValueChange?.(chosen)
		}
	}

	function selectItem(item, e, { triggerCallback = true } = {}) {
		e?.preventDefault()
		e?.stopPropagation()
		value = item[valueKey]
		query = item[labelKey]
    closeList()
    if (triggerCallback) onValueChange?.(item)
	}

  function closeList() {
    open = false
    index = 0
  }

  export function setItem(item, { triggerCallback = false } = {}) {
    value = item[valueKey]
    query = item[labelKey]
    if (triggerCallback) onValueChange?.(item)
  }

  export function setValue(value, { triggerCallback = false } = {}) {
    const item = items.find(item => item[valueKey] === value)
    if (!item) return
    value = value
    query = item[labelKey]
    if (triggerCallback) onValueChange?.(value)
  }

  export function clearValue({ triggersCallback = false, clearItems = false } = {}) {
    value = null
    query = ""
    if (clearItems) items = []
    if (triggersCallback) {
      onClear?.()
      onValueChange?.(value)
    }
  }

  export function focus() {
    // open = true
    inputRef?.focus();
  }

  // Use watch to see if there is a url and it's changed, call fetchFromUrl
  watch(() => url, (newUrl, oldUrl) => {
    if ((newUrl !== oldUrl) && fetchOnUrlChange) {
      fetchFromUrl()
    }
  })

</script>

<Popover bind:open placement="bottom-start">
	{#snippet trigger()}
    <div class="input-container" class:input-error={error}>
      <input
        bind:this={inputRef}
        bind:value={query}
        class="input"
        onfocus={() => open = true}
        onclick={() => open = true}
        onblur={handleBlur}
        onkeydown={handleKeydown}
        oninput={handleInput}
        name={name}
        autocomplete="off"
        {placeholder}
        {...inputProps}
      />
      {#if loading || typing}
        <div class="loading">
          {#if loading}
            <LoaderCircle size={16} /> 
          {:else}
            <Ellipsis size={16} />
          {/if}
        </div>
      {/if}
    </div>
	{/snippet}
	
  {#snippet contents({ maxHeight })}
    {@const preferredMaxHeight = Math.min(maxHeight, 300) }
    {#if query.length > 0 || filteredItems.length > 0}
		<div class="list" style={`max-height: ${preferredMaxHeight}px`}>
      {#each filteredItems as item, idx (item[valueKey])}
      <div 
        bind:this={itemRefs[idx]}
        class="item" 
        class:highlighted={idx === index}
        class:selected={item[valueKey] === value}
        onpointerdown={(e) => selectItem(item, e)}
        >{item[labelKey]}</div>
        {:else}
        <div class="item no-results">{items.length ? "No results" : "No items available"}</div>
      {/each}
		</div>
    {/if}
	{/snippet}
</Popover>

<p class="pl-1 text-[11px] text-red-500/80 error-text empty:hidden">{error}</p>

{#if name}
  <input type="hidden" name={name} value={value ?? ""} />
{/if}

<style>
	.list {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-radius: 0.5rem;
    padding: 0.4rem 0;
    border: 1px solid var(--color-gray-100);
    background: white;
    overflow-y: auto;
    width: 100%;
    &.empty {
      padding: 0;
      border: none;
      box-shadow: none;
    }
	}
	.item {
		background: white;
		color: var(--color-gray-700);
		padding: 0.5rem 0.85rem;
		font-size: 0.8rem;
    border-bottom: 1px solid var(--color-gray-100);
    cursor: pointer;
    &:last-child {
      border-bottom: none;
    }
    /* &:hover {
      background: var(--color-gray-100);
    } */
		&.highlighted {
			background: var(--color-blue-50);
			color: var(--color-gray-800);
		}
    &.selected {
      background: var(--color-blue-50);
      color: var(--color-gray-800);
    }
    &.no-results {
      color: var(--color-gray-500);
      padding: 0.4rem 0.85rem;
      font-size: 0.8rem;
      text-align: center;
      pointer-events: none;
    }
	}

  .loading {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-gray-500);
  }
</style>